import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import { waitForVoice } from '../utils/audios'
import stories from '../../data/stories'
import AlphabetScreen from './AlphabetScreen'
import WordsScreen from './WordsScreen'
import part1 from '../../data/part1'
import part2 from '../../data/part2'

// stories are now imported from '../../data/stories'

var speechConfig = SpeechSDK.SpeechConfig.fromSubscription("3e60cc9987ec41418be6de7cf1905fa2", "eastus");
speechConfig.setProperty(SpeechSDK.PropertyId.SpeechServiceConnection_EndSilenceTimeoutMs, "5000");
speechConfig.setProperty(SpeechSDK.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs, "5000");

speechConfig.setProperty("SPEECH-PhraseTimeout", "100000");

// Normalize a word: strip punctuation, lowercase, collapse spaces
function normalizeWord(w) {
  if (!w && w !== 0) return '';
  return String(w)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, '');
}

// Simple Levenshtein distance for fuzzy token match
function levenshtein(a = '', b = '') {
  a = String(a);
  b = String(b);
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    const ca = a.charCodeAt(i - 1);
    for (let j = 1; j <= n; j++) {
      const cb = b.charCodeAt(j - 1);
      const cost = ca === cb ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1, // deletion
        dp[i][j - 1] + 1, // insertion
        dp[i - 1][j - 1] + cost // substitution
      );
    }
  }
  return dp[m][n];
}

// Shallow equality for simple key->value maps
function shallowEqual(a = {}, b = {}) {
  if (a === b) return true;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (let k of aKeys) {
    if (a[k] !== b[k]) return false;
  }
  return true;
}

// Port of bestSentenceMatcher (word-level) for JS: builds a map of main word+index -> boolean
function bestSentenceMatcherJS(main, test) {
  // Normalize inputs similarly to the Python version
  const normMain = String(main || '')
    .replace(/\n/g, ' ')
    .replace(/[^A-Za-z0-9_ -]+/g, '')
    .trim();
  const normTest = String(test || '')
    .replace(/\n/g, ' ')
    .replace(/[^A-Za-z0-9_ -]+/g, '')
    .trim();

  const splittedM = normMain.split(/\s+/).map(normalizeWord);
  const splittedT = normTest.split(/\s+/).map(normalizeWord);

  // LCS with fuzzy equality (levenshtein <= 1 or substring containment for short diffs)
  const m = splittedM.length;
  const n = splittedT.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  const equal = (x, y) => {
    if (!x || !y) return false;
    if (x === y) return true;
    const d = levenshtein(x, y);
    if (d <= 1) return true;
    if (x.length > 3 && y.length > 3 && (x.includes(y) || y.includes(x))) return true;
    return false;
  };
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (equal(splittedM[i - 1], splittedT[j - 1])) dp[i][j] = dp[i - 1][j - 1] + 1;
      else dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  // Backtrack to get matched indices in main
  const matchedMainIdx = new Set();
  let i = m, j = n;
  while (i > 0 && j > 0) {
    if (equal(splittedM[i - 1], splittedT[j - 1])) {
      matchedMainIdx.add(i - 1);
      i -= 1; j -= 1;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      i -= 1;
    } else {
      j -= 1;
    }
  }

  // Build output map keyed by normalize(word)+index
  const wrds = {};
  for (let k = 0; k < splittedM.length; k++) {
    const key = `${splittedM[k]}${k}`;
    wrds[key] = matchedMainIdx.has(k);
  }
  return wrds;
}



async function getMicClip({ microPhoneConfig: _micConfig, setStatus, setHasRecorded, audioStreamBegan, closeStream }) {
  const microPhoneConfig = _micConfig || {};
  return await waitForVoice(microPhoneConfig.wrLength, 1, microPhoneConfig.type, microPhoneConfig.waitTime, {
    speechTimeOut: microPhoneConfig.timeOut,
    listening() {
      setStatus("recording")
      setHasRecorded && setHasRecorded(true)
    },
    empty() {
      setStatus("recording")
    },
    audioStreamBegan,
    closeStream
  });
}

const ReadingScreen = ({ onComplete, gradeLevel = 1, attemptNumber = 1, fallbackActive = false, onFallbackDone, preMessageText }) => {
  const [timeLeft, setTimeLeft] = useState(62);
  const endReasonRef = useRef(null); // 'completed' | 'timeout' | null
  const didCompleteRef = useRef(false);
  // Removed spokenWords and setSpokenWords
  // Stop recording handler to end stream and compute result
  const handleStop = useCallback(() => {
    if (typeof window.suspendRecordingAudio === 'function') {
      window.suspendRecordingAudio(true);
    }
  }, []);

  // moved below after status declaration

  const storyData = stories[gradeLevel] || stories[1];
  const readingText = typeof storyData === 'string' ? storyData : (storyData && storyData.text) || '';
  const allWords = useMemo(() => readingText.replace(/\n/g, ' ').split(/\s+/), [readingText]);


  // ...existing code...

  // State and refs


  // State and refs
  const [status, setStatus] = useState(null)
  const silences = useRef(0)
  const [sentenceResults, setSentenceResults] = useState(null)
  const [phonemeResults, setPhonemeResults] = useState({})
  const sentenceResultsRef = useRef(null)
  const phonemeResultsRef = useRef({})
  const progressIndexRef = useRef(0)
  const audioBlob = useRef()
  const metaBlob = useRef()
  const startTime = useRef()
  const [finalWordMap, setFinalWordMap] = useState(null)
  const finalComputedRef = useRef(false)
  const [showAlphabet, setShowAlphabet] = useState(false);
  // const [showWords, setShowWords] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  const [countdown, setCountdown] = useState(null); // null or number
  const transitionAudioRef = useRef(null);
  const [preMessage, setPreMessage] = useState(null);

  // Start the countdown only after reading actually begins (not during pre-audio overlays)
  useEffect(() => {
    if (!(status === 'started' || status === 'recording')) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          // Always set endReasonRef to 'timeout' on timer expiry
          endReasonRef.current = 'timeout';

          // Play ding audio on timeout
          try {
            const dingAudio = new Audio(process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/audio/ding.mp3` : '/audio/ding.mp3');
            dingAudio.play().catch(() => { });
          } catch { }

          // Only stop and set status if not already processing
          if (status !== 'processing') {
            handleStop();
            setStatus('processing');
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [status, handleStop]);

  // Use live progress during recording; after recording, use local word-matcher over meta transcript
  const wordStatuses = useMemo(() => {
    const statuses = Array(allWords.length).fill('pending');
    const sr = sentenceResults && sentenceResults[gradeLevel];
    const isFinal = (status === 'processing') && !!finalWordMap;

    if (!isFinal) {
      if (!sr) return statuses;
      for (let i = 0; i < allWords.length; i++) {
        const key = `${normalizeWord(allWords[i])}${i}`;
        if (sr[key] === 'read') {
          statuses[i] = 'progress';
        }
      }
      return statuses;
    }

    // Final evaluation mode using finalWordMap
    for (let i = 0; i < allWords.length; i++) {
      const key = `${normalizeWord(allWords[i])}${i}`;
      if (finalWordMap && Object.prototype.hasOwnProperty.call(finalWordMap, key)) {
        statuses[i] = finalWordMap[key] ? 'spoken' : 'wrong';
      } else {
        statuses[i] = 'unread';
      }
    }
    return statuses;
  }, [allWords, sentenceResults, gradeLevel, status, finalWordMap]);

  // Speech Recognition setup omitted to avoid conflicts (Azure SDK used instead)




  //   function matchStrings(a, b, handleEndings = true) {
  //     // initialize an empty object to store the results
  //     let result = {};
  //     // initialize two pointers to track the indices of a and b
  //     let i = 0;
  //   // let j = 0 // unused
  //     // loop through the list a until the end
  //     var lastRead = null;
  //     while ((i < b.length) && (i < a.length)) {
  //         lastRead = `${a[i]}_${i}`.trim().replace(/[^A-Za-z0-9_ -]+/, '').toLowerCase();
  //         result[`${a[i]}${i}`] = "read";
  //         i++;
  //     }
  //     return [result, lastRead]
  // }





  const microphoneOperation = useCallback(async () => {

    // s3FieldsRef.current = await initVoice(lessonId, current)
    var recognizer;
    var recognizingText = readingText;

    if (typeof recognizingText != "string") {
      recognizingText = recognizingText.join(" ")
    }
    setSentenceResults((prev) => {
      const prevForGrade = (prev && prev[gradeLevel || 1]) || undefined;
      if (prevForGrade && Object.keys(prevForGrade).length === 0) {
        sentenceResultsRef.current = prev;
        return prev;
      }
      const next = { ...(prev || {}), [gradeLevel || 1]: {} };
      sentenceResultsRef.current = next;
      return next;
    })
    recognizingText = recognizingText.trim().replace(/[^A-Za-z0-9_ -]+/ig, '').toLowerCase()

    // Normalize target words based on rendered text
    const renderedWords = allWords
    const targetNormalized = renderedWords.map(w => normalizeWord(w))
    progressIndexRef.current = 0



    const blob = await getMicClip({
      microPhoneConfig: {
        waitTime: 10000,
        timeOut: 62000,
        wrLength: allWords.length,
        type: "norepeat",
      },
      setStatus,
      // setHasRecorded, // removed unused
      audioStreamBegan(stream, endTheStream) {
        startTime.current = new Date()

        var audioConfig = SpeechSDK.AudioConfig.fromStreamInput(stream);
        recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);
        const phraseList = SpeechSDK.PhraseListGrammar.fromRecognizer(recognizer);
        var mainTarget = recognizingText.split(" ");
        for (let i = 0; i < mainTarget.length; i++) {
          phraseList.addPhrase(mainTarget[i]);
        }

        // const conn = SpeechSDK.Connection.fromRecognizer(recognizer);
        // conn.setMessageProperty("speech.context", "phraseDetection", {
        //     "INTERACTIVE": {
        //         "segmentation": {
        //             "mode": "custom",
        //             "segmentationSilenceTimeoutMs": 5000
        //         }
        //     },
        //     mode: "Interactive"
        // });

        // Enable Pronunciation Assessment
        const pronunciationAssessmentConfig = new SpeechSDK.PronunciationAssessmentConfig(
          recognizingText,
          SpeechSDK.PronunciationAssessmentGradingSystem.HundredMark,
          SpeechSDK.PronunciationAssessmentGranularity.Word,
          "true"
        );
        pronunciationAssessmentConfig.properties.setProperty(
          SpeechSDK.PropertyId.PronunciationAssessment_EnableMiscue,
          "true"
        );
        pronunciationAssessmentConfig.applyTo(recognizer);

        // var totalProcessedTextIs = [] // unused
        setSentenceResults((prev) => {
          const prevForGrade = (prev && prev[gradeLevel || 1]) || undefined;
          if (prevForGrade && Object.keys(prevForGrade).length === 0) {
            sentenceResultsRef.current = prev;
            return prev;
          }
          const next = { ...(prev || {}), [gradeLevel || 1]: {} };
          sentenceResultsRef.current = next;
          return next;
        })
        var noticedWords = {};


        for (let i = 0; i < mainTarget.length; i++) {
          var word = mainTarget[i]
          noticedWords[i] = { word, noticed: false }
        }
        var allTexts = []
        var currentTextItem = "";
        var allTextPrevMerged = ""
        recognizer.recognizing = function (s, e) {
          // handle intermediate results

          if (typeof (e.result.text) == "string") {
            try {
              var prctext = e.result.text.trim().replace(/[^A-Za-z0-9_ -]+/ig, '').toLowerCase()
              currentTextItem = prctext

              var recognizedText = ([allTextPrevMerged, prctext]).filter(itm => itm?.length > 0).join(" ")
              var recognizedWords = recognizedText.split(" ")
              var recognizedNorm = recognizedWords.map(w => normalizeWord(w))

              // Live progress: contiguous from start, no skipping; combine with existing progressIndex
              const contiguousLen = Math.min(targetNormalized.length, recognizedNorm.length)
              const nextProgress = Math.max(progressIndexRef.current || 0, contiguousLen)
              const sResultItem = {}
              let lastRead = null
              for (let i = 0; i < nextProgress; i++) {
                const norm = targetNormalized[i]
                sResultItem[`${norm}${i}`] = 'read'
                lastRead = `${norm}_${i}`
              }
              if (lastRead) {
                const el = document.getElementById(lastRead)
                if (el) {
                  try { el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' }) } catch { }
                }
              }
              setSentenceResults((prev) => {
                const key = gradeLevel || 1;
                const prevForGrade = (prev && prev[key]) || {};
                if (shallowEqual(prevForGrade, sResultItem)) {
                  sentenceResultsRef.current = prev;
                  return prev;
                }
                const next = { ...(prev || {}), [key]: sResultItem };
                sentenceResultsRef.current = next;
                return next;
              })
            } catch (e) {
              console.error(e)
            }
          }
          // handle intermediate results
        };

        // Removed unused arrays totalProcessedTexts, totalScorings, totalWords

        recognizer.recognized = function (s, e) {
          try {
            let jsonStr = null;
            try {
              jsonStr = e.result.properties.getProperty(SpeechSDK.PropertyId.SpeechServiceResponse_JsonResult)
            } catch { }
            if (!jsonStr && e.result.json) {
              jsonStr = e.result.json;
            }
            var data = jsonStr ? JSON.parse(jsonStr) : null
            allTexts.push(currentTextItem)
            currentTextItem = null;
            allTextPrevMerged = allTexts.filter(itm => itm?.length > 0).join(" ")
            if (data && data.RecognitionStatus === "Success") {

              const nBest = data.NBest && data.NBest[0]
              const words = (nBest && nBest.Words) || []
              if (words.length > 0) {
                // Build updates locally to avoid setState inside loops
                const existing = (phonemeResultsRef.current && phonemeResultsRef.current[gradeLevel]) ? [...phonemeResultsRef.current[gradeLevel]] : Array(renderedWords.length).fill(null)
                const cur = (sentenceResultsRef.current && sentenceResultsRef.current[gradeLevel]) ? { ...sentenceResultsRef.current[gradeLevel] } : {}
                let p = progressIndexRef.current || 0

                // Align strictly in order: only accept the next expected word (no skipping)
                for (const w of words) {
                  const recNorm = normalizeWord(w.Word)
                  if (!recNorm) continue
                  if (p < targetNormalized.length && targetNormalized[p] === recNorm) {
                    existing[p] = {
                      score: (w.PronunciationAssessment && typeof w.PronunciationAssessment.AccuracyScore === 'number') ? w.PronunciationAssessment.AccuracyScore : null,
                      error: w.PronunciationAssessment ? w.PronunciationAssessment.ErrorType : undefined
                    }
                    cur[`${targetNormalized[p]}${p}`] = 'read'
                    // const lastIdToScroll = `${targetNormalized[p]}_${p}`
                    p = p + 1
                  }
                  // If not matching the next expected word, ignore for live progression.
                }
                progressIndexRef.current = Math.max(progressIndexRef.current || 0, p)
                setPhonemeResults(prev => ({ ...(prev || {}), [gradeLevel]: existing }))
                setSentenceResults(prev => {
                  const prevForGrade = (prev && prev[gradeLevel]) || {};
                  if (shallowEqual(prevForGrade, cur)) {
                    sentenceResultsRef.current = prev;
                    return prev;
                  }
                  const next = { ...(prev || {}), [gradeLevel]: cur };
                  sentenceResultsRef.current = next;
                  return next;
                })
                // keep refs in sync
                phonemeResultsRef.current = { ...(phonemeResultsRef.current || {}), [gradeLevel]: existing }
                sentenceResultsRef.current = { ...(sentenceResultsRef.current || {}), [gradeLevel]: cur }
                // if (lastIdToScroll) {
                //   const el = document.getElementById(lastIdToScroll)
                //   if (el) {
                //     try { el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' }) } catch {}
                //   }
                // }
              }


              // var [accuracy, complete, fluency, pronunciationScore] = totalScorings.reduce((prev, curr)=>{
              //     return [prev[0] + curr[0], prev[1] + curr[1],prev[2] + curr[2],prev[3] + curr[3]]
              // }, [0,0,0,0])
              metaBlob.current = {
                text: allTextPrevMerged,
                duration: metaBlob.current?.duration ? metaBlob.current.duration + e.result.duration : e.result.duration,
                wcpm: ((allTextPrevMerged.split(" ").length / (e.result.duration / 10000000)) * 60)
              }

              if ((allTextPrevMerged.split(" ").slice(-1)[0] === recognizingText.split(" ").slice(-1)[0])) {
                endReasonRef.current = 'completed';
                // Enter processing state so UI freezes blue and final scoring can run
                setStatus('processing');
                endTheStream()
              }

            } else if (data && data.RecognitionStatus === "EndOfDictation") {
              if (!endReasonRef.current) endReasonRef.current = 'timeout';
              endTheStream()
            }

          } catch (e) {
            console.error(e)
          }

        };
        recognizer.canceled = function (s, e) {
          // handle cancelation
          endTheStream()
        };
        recognizer.startContinuousRecognitionAsync()




      },
      async closeStream() {
        // if (itemDetails?.type == "sentence") {
        await (new Promise((resolve, reject) => {
          recognizer.stopContinuousRecognitionAsync(resolve, reject)
        }))
        // }
      }
    })
    // Simple wait function for delay in milliseconds
    function wait(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }
    await wait(1)
    // if (itemDetails?.type == "sentence") {
    await (new Promise((resolve, reject) => {
      recognizer.stopContinuousRecognitionAsync(resolve, reject)

    }))
    recognizer.close()
    // }
    if (blob) {
      if (blob === "done") {
        // setStatus("started")

      } else {
        audioBlob.current = blob;
        setStatus("processing")
      }
    }
    else {
      setStatus(null)

      // if (silences.current < 1) {
      //   // if (stageInfo.silencePrompt) {
      //   //     await soundManager.playFrom("status", stageInfo.silencePrompt)
      //   // }
      //   silences.current += 1
      //   setStatus("started")
      // } else if (silences.current === 1) {
      //   silences.current += 1
      //   setStatus("started")

      // } else {
      //   setStatus("started")
      //   // window.location.reload()
      //   // audioBlob.current = blob;
      //   // setStatus("processing")
      // }

    }
  }, [gradeLevel, allWords, readingText]);

  useEffect(() => {
    if (fallbackActive) {
      // Stop any ongoing recognition and switch to fallback steps
      setStatus(null);
      setShowAlphabet(true);
      // setShowWords(false);
      return;
    }

    if (status === "processing") {
      // stop()
    }


    if (readingText && (status === "started" || status === "processing")) {
      (async () => {




        if ((status === "started")) {

          // if ((!stageInfo.microphone) && (!itemDetails.microphone)) {
          await microphoneOperation()
          // } else {
          // reset(false, (itemDetails.timeOut || stageInfo.timeOut || 20), current)
          // }

        } else if (status === "processing") {
          // const { url, uploadId } = s3FieldsRef.current
          // var isUploaded = false; // unused
          // soundManager.playSound("processing", "processing", true, 0.06)
          if (audioBlob.current) {
            // await uploadToS3(url, audioBlob.current, "audio.wav", "audio/x-wav")
            // isUploaded = true; // unused
          }
          // Build final word map using local matcher against meta transcript
          if (!finalComputedRef.current) {
            finalComputedRef.current = true;
            const metaText = metaBlob.current?.text || '';
            const fm = bestSentenceMatcherJS(readingText, metaText);
            setFinalWordMap(fm);
            try {
              const matchedCount = fm ? Object.values(fm).filter(Boolean).length : 0;
            } catch { }

            // Do not call onComplete here; defer to a top-level effect which waits 2s to display coloring
          }
        }


      })()

    }



  }, [gradeLevel, status, microphoneOperation, allWords, onComplete, phonemeResults, sentenceResults, readingText, fallbackActive, attemptNumber, storyData]);

  // After processing state and final word map available, wait 2s to show coloring, then notify parent with finishReason
  useEffect(() => {
    if (status !== 'processing') return;
    if (!finalWordMap) return;
    if (didCompleteRef.current) return;
    const t = setTimeout(async () => {
      if (didCompleteRef.current) return;
      didCompleteRef.current = true;

      const finishReason = endReasonRef.current || 'completed';
      const metaText = metaBlob.current?.text || '';

      // Determine what passage to send to API
      let passageToSend = '';
      if (metaText.trim()) {
        // Send any recognized text if available (whether completed or timed out)
        passageToSend = metaText.trim();
      } else {
        // No recognized text - send empty string as requested
        passageToSend = ' ';
      }

      try {
        // Call the API to get proper scoring
        const response = await fetch('https://frc.api.read21.org/api/score/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            passage: passageToSend,
            grade: gradeLevel
          })
        });

        if (!response.ok) {
          throw new Error(`API call failed: ${response.status}`);
        }

        const apiResult = await response.json();

        if (apiResult.success && apiResult.data) {
          // Use API results
          const { wpm, accuracy, passed } = apiResult.data;
          const total = allWords.length;
          const matched = Math.round((accuracy / 100) * total); // Convert accuracy back to matched words for display
          const passWords = Math.round((accuracy / 100) * total); // For display purposes

          if (typeof onComplete === 'function') {
            onComplete({
              pass: passed,
              accuracyAvg: accuracy,
              wcpm: wpm,
              grade: gradeLevel,
              matched,
              total,
              passWords,
              finishReason
            });
          }
        } else {
          throw new Error('API returned unsuccessful response');
        }
      } catch (error) {
        console.error('API scoring failed, falling back to local calculation:', error);

        // Fallback to local calculation if API fails
        const fm = finalWordMap;
        const total = allWords.length;
        let matched = 0;
        for (let i = 0; i < total; i++) {
          const key = `${normalizeWord(allWords[i])}${i}`;
          if (fm && fm[key]) matched++;
        }
        const accuracyAvg = total > 0 ? (matched / total) * 100 : 0;
        const defaultPassWords = Math.ceil((total || 0) * 0.7);
        const passWords = (typeof storyData === 'object' && storyData && typeof storyData.passWords === 'number') ? storyData.passWords : defaultPassWords;
        const pass = matched >= passWords;
        const wcpm = metaBlob.current?.wcpm;

        if (typeof onComplete === 'function') {
          onComplete({ pass, accuracyAvg, wcpm, grade: gradeLevel, matched, total, passWords, finishReason });
        }
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [status, finalWordMap, allWords, storyData, gradeLevel, onComplete]);

  // Handlers for inline fallback flow
  const handleAlphabetCompleteInline = ({ passedAll, correctCount, totalQuestions }) => {
    // Store alphabet results to include in the final result
    const alphabetResults = { correctCount, totalQuestions };

    setShowAlphabet(false);
    // setShowWords(false);
    onFallbackDone && onFallbackDone(alphabetResults);

    // // Always proceed to part 2 (words) regardless of alphabet performance
    // setShowAlphabet(false);
    // setShowTransition(true);
    // // Play part2.mp3 as transition, then start countdown
    // const audio = new Audio(process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/audio/part2.mp3` : '/audio/part2.mp3');
    // transitionAudioRef.current = audio;
    // const onEnd = () => {
    //   setShowTransition(false);
    //   setCountdown(3);
    // };
    // audio.addEventListener('ended', onEnd);
    // audio.addEventListener('error', onEnd);
    // audio.play().catch(onEnd);
  };

  // const handleWordsCompleteInline = () => {
  //   setShowAlphabet(false);
  //   setShowWords(false);
  //   onFallbackDone && onFallbackDone();
  // };

  // Auto-start logic with pre-second-attempt audio cue (only on retry of same grade)
  // Show overlay "Now read it fast" and play readfaster.mp3 before second attempt
  useEffect(() => {
    // If second attempt, always show cue + play audio, regardless of parent preMessageText
    if (attemptNumber > 1) {
      const message = 'Read it a little faster.';
      setPreMessage(message);
      try {
        const audio = new Audio(process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/audio/readfaster.mp3` : '/audio/readfaster.mp3');
        const onEnd = () => {
          setPreMessage(null);
          setStatus('started');
        };
        audio.addEventListener('ended', onEnd);
        audio.addEventListener('error', onEnd);
        audio.play().catch(onEnd);
        return () => {
          audio.removeEventListener('ended', onEnd);
          audio.removeEventListener('error', onEnd);
          try { audio.pause(); } catch { }
        };
      } catch {
        setPreMessage(null);
        setStatus('started');
      }
      return;
    }

    // For first attempt: if parent provided a preMessage (e.g., Grade X), show briefly then start
    if (preMessageText) {
      setPreMessage(preMessageText);
      const t = setTimeout(() => {
        setPreMessage(null);
        setStatus('started');
      }, 2000);
      return () => clearTimeout(t);
    }

    // Default immediate start
    setStatus('started');
  }, [attemptNumber, preMessageText]);

  // removed isRecording state and handleRecStop as they were unused
  // If fallback is active, render embedded Alphabet/Words components instead of recording UI
  // Countdown effect for fallback word questions
  useEffect(() => {
    if (countdown === null) return;
    if (countdown === 0) {
      setCountdown(null);
      // setShowWords(true);
      return;
    }
    const timer = setTimeout(() => setCountdown(c => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  if (fallbackActive && (showAlphabet || showTransition || countdown !== null)) {
    return (
      <div className="content-area">
        {showAlphabet && (
          <AlphabetScreen items={part1} onComplete={handleAlphabetCompleteInline} />
        )}
        {showTransition && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <div style={{ fontSize: 32, fontWeight: 600, margin: 40 }}>Get ready for the word questions...</div>
            {/* Optionally add a spinner or animation here */}
          </div>
        )}
        {/* {countdown !== null && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <div style={{ fontSize: 32, fontWeight: 600, margin: 40 }}>Starting in</div>
            <div style={{ fontSize: 80, fontWeight: 700, color: 'var(--primary-color)' }}>{countdown}</div>
          </div>
        )} */}
        {/* {showWords && !showTransition && (
          <WordsScreen items={part2} onComplete={handleWordsCompleteInline} />
        )} */}
      </div>
    );
  }

  return (
    <div className="content-area">
      {preMessage && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ fontSize: 42, fontWeight: 800, color: 'var(--primary-color)', textAlign: 'center', padding: 20 }}>
            {preMessage}
          </div>
        </div>
      )}
      {/* Processing overlay before final map is ready */}
      {status === 'processing' && !finalWordMap && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} aria-live="polite" aria-busy="true">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ fontSize: 36, fontWeight: 800, color: 'var(--primary-color)', marginBottom: 12 }}>
              Scoring…
            </div>
            <div style={{ width: 36, height: 36, borderRadius: '50%', border: '4px solid rgba(25,118,210,0.25)', borderTopColor: 'var(--primary-color)', animation: 'spin 1s linear infinite' }} />
          </div>
        </div>
      )}
      {/* Timer */}
      <div className="timer">
        <span className="timer-number">{timeLeft}</span>
        {/* Clock icon */}
        <svg width="29" height="29" viewBox="0 0 29 29" fill="none">
          <circle cx="14.5" cy="14.5" r="10.4213" stroke="var(--primary-color)" strokeWidth="3" />
          <path d="M2 5L7.01953 0.607959" stroke="var(--primary-color)" strokeWidth="3" strokeLinecap="round" />
          <path d="M14.8623 16V9.72583" stroke="var(--primary-color)" strokeWidth="3" strokeLinecap="round" />
          <path d="M10.1564 14.7456L14.8621 16.0004" stroke="var(--primary-color)" strokeWidth="3" strokeLinecap="round" />
          <path d="M5.94385 26.1906L7.46667 24.007" stroke="var(--primary-color)" strokeWidth="3" strokeLinecap="round" />
          <path d="M27.544 5L22.525 0.607959" stroke="var(--primary-color)" strokeWidth="3" strokeLinecap="round" />
          <path d="M23.6006 26.1906L22.0778 24.007" stroke="var(--primary-color)" strokeWidth="3" strokeLinecap="round" />
        </svg>
      </div>

      {/* Controls */}
      {/* <div className="scroll-indicator" style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
        <div
          className="stop-button"
          onClick={handleStop}
          title="Stop Recording"
        >
          <div className="stop-icon"></div>
        </div>
      </div> */}

      {/* Main content */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        flex: 1,
        padding: '20px',
        paddingTop: '100px', // Space for timer
        maxWidth: '100%',
      }}>
        {/* Reading text */}
        <div
          style={{
            flex: 1,
            maxWidth: '900px',
            minHeight: '500px',
            margin: '0 auto',
            overflowY: 'auto',
            padding: 0,
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE 10+
          }}
          className="reading-scrollbox"
        >
          <div className="reading-text">
            {allWords.map((word, idx) => {
              let color, fontWeight;
              // Live progress highlight (blue) while recording
              if (wordStatuses[idx] === 'progress') {
                color = '#1976d2'; // blue for in-progress reading
              } else if (wordStatuses[idx] === 'spoken') {
                color = '#2e7d32'; fontWeight = 700; // green for correct (final)
              } else if (wordStatuses[idx] === 'wrong') {
                color = '#c62828'; fontWeight = 700; // red for wrong (final)
              } else if (wordStatuses[idx] === 'unread') {
                color = '#9e9e9e'; // gray for unread (final)
              }
              return (
                <span
                  key={idx}
                  style={{
                    color,
                    fontWeight,
                    marginRight: 4,
                  }}
                  id={`${normalizeWord(word)}_${idx}`}
                >
                  {word}
                  {/* {score !== null && score !== undefined && (
                    <span style={{ fontSize: '0.7em', color: '#888', marginLeft: 2 }}>
                      ({Math.round(score)})
                    </span>
                  )} */}
                  {' '}
                </span>
              );
            })}
          </div>
        </div>

        {/* {isRecording && (
          <div
            className="stop-button"
            onClick={handleRecStop}
            style={{
              animation: isRecording ? 'pulse 2s infinite' : 'none'
            }}
          >
            <div className="stop-icon"></div>
          </div>
        )} */}

        {/* Status message when stopped */}
        {/* {!isRecording && (
          <div style={{
            color: 'var(--primary-color)',
            fontFamily: 'var(--font-alumni)',
            fontSize: '24px',
            fontWeight: '700',
            textAlign: 'center'
          }}>
            Recording stopped
          </div>
        )} */}
      </div>


    </div>
  );
};

export default ReadingScreen;
